// Lightweight Points-based particle emitter. Deterministic per `seed`: traits
// come from the seeded generator and positions are pure functions of elapsed
// time (no Math.random at render time), satisfying replay and strict-purity.
import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import { AdditiveBlending, BufferAttribute, type Points } from 'three';

import {
  buildParticleSeeds,
  particlePosition,
  type FxEmitterPreset,
} from './particles';

export interface ParticleEmitterProps {
  preset: FxEmitterPreset;
  /** Particle count, clamped 1..200. */
  rate: number;
  /** Point size in centimetres. */
  size: number;
  colour: string;
  /** Deterministic seed; the same seed replays identically. */
  seed: number;
}

export function ParticleEmitter({ preset, rate, size, colour, seed }: ParticleEmitterProps) {
  const count = Math.max(1, Math.min(200, Math.round(rate)));
  const seeds = useMemo(() => buildParticleSeeds(seed, count), [seed, count]);
  const positions = useMemo(() => {
    const array = new Float32Array(count * 3);
    for (let i = 0; i < seeds.length; i++) {
      const [x, y, z] = particlePosition(preset, seeds[i]!, 0);
      array.set([x, y, z], i * 3);
    }
    return array;
  }, [seeds, preset, count]);
  const pointsRef = useRef<Points>(null);

  useFrame(({ clock }) => {
    const points = pointsRef.current;
    if (!points) return;
    const attribute = points.geometry.getAttribute('position') as BufferAttribute | undefined;
    if (!attribute) return;
    const t = clock.elapsedTime;
    for (let i = 0; i < seeds.length; i++) {
      const [x, y, z] = particlePosition(preset, seeds[i]!, t);
      attribute.setXYZ(i, x, y, z);
    }
    attribute.needsUpdate = true;
  });

  return (
    <points ref={pointsRef} key={`${preset}-${count}-${seed}`}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        color={colour}
        size={Math.max(0.02, size / 100)}
        sizeAttenuation
        transparent
        opacity={0.9}
        depthWrite={false}
        blending={AdditiveBlending}
      />
    </points>
  );
}
